import React from 'react';
import {connect} from 'react-redux';
import {FormattedMessage} from 'react-intl';
import styled from 'styled-components';
import {NavLink, withRouter} from 'react-router-dom';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {moduleRoles} from '../../../utils/roles';
import PropTypes from 'prop-types';
import {
    ADMINISTRATION_PATH,
    HOME_PATH,
    ORDERS_PATH,
    DOCUMENTATIONS_PATH,
    CLAIMS_PATH
} from '../../../routes/paths';
import {PRIMARY_DARK_GREY, PRIMARY_YELLOW} from '../../../theme/colors';
import {domains, providers} from '../../../constants/Utils';
import commonMessages from '../../../intl/common/commonMessages';
import menuMessages from '../../../intl/layout/menuMessages';
import SupportModal from './SupportModal';
import {closeSupportModal, showSupportModal, saveUserSettings, saveProfile} from '../../../actions/actionSupport';
import {initiateLogout} from '../../../utils/auth';

const TopNavigation = styled.nav`
    font-family: sans-serif;
    font-weight: bold;
    padding: 1px 10px;
    background-color: ${PRIMARY_DARK_GREY};
`;

const UserNav = styled.div`
    font-family: sans-serif;
    font-weight: normal;
    color: rgba(255, 255, 255, 0.85);
    margin: 5px 0px 5px 5px;
    display: block;
    padding: .5rem 0rem .5rem 1rem;
`;

const MenuLink = styled(NavLink)`
    :visited,
    :link {
        color: rgba(255, 255, 255, 0.85);
    }
    :active,
    :hover {
        background-color: ${PRIMARY_YELLOW};
        color: black;
    }
    transition: all 0.5s ease;

    && {
        padding: 0.6em 1em;
        margin: 5px;
    }

    &.${'nav-item-active'} {
        background-color: ${PRIMARY_YELLOW};
        color: black;
    }
`;

const CollapseButton = styled.button`
    color: rgba(255, 255, 255, 0.7);
    border-color: rgba(255, 255, 255, 0.7);
    border-radius: .25em;
`;

const SupportButton = styled.div`
    cursor: pointer;
    :visited {
        color: rgba(255, 255, 255, 0.85);
    }
    :active,
    :hover {
        background-color: ${PRIMARY_YELLOW};
        color: black;
    }
    transition: all 0.5s ease;

    && {
        margin: 5px;
        display: block;
        padding: .5rem;
        border-radius: .25rem;
    }
    color: white;
`;

function TopBar({userDetail: {roles, name, ipn}, isModalOpen, showSupportModal, closeSupportModal, language, locale, saveUserSettings, saveProfile}) {
    return (
        <TopNavigation className="navbar navbar-expand-md nav-pills">
            <MenuLink exact className="nav-link" to={HOME_PATH} activeClassName="nav-item-active">
                <FontAwesomeIcon icon="home" size="lg"/>
            </MenuLink>
            <CollapseButton className="navbar-toggler" type="button" data-toggle="collapse" data-target="#navbarNav"
                            aria-controls="navbarNav" aria-expanded="false" aria-label="Toggle navigation">
                <FontAwesomeIcon icon="bars" size="lg"/>
            </CollapseButton>
            <div className="collapse navbar-collapse" id="navbarNav">
                <ul className="navbar-nav mr-auto">
                    {(moduleRoles.isAdmin(roles) || moduleRoles.isPowerUser(roles) || moduleRoles.isSGAdmin(roles)) &&
                    <li className="nav-item">
                        <MenuLink className="nav-link" to={ADMINISTRATION_PATH} activeClassName="nav-item-active">
                            <FormattedMessage {...menuMessages.ADMINISTRATION}/>
                        </MenuLink>
                    </li>}
                    {(moduleRoles.isEditorOrder(roles) || moduleRoles.isSGEditorOrder(roles) || moduleRoles.isDealerOrder(roles)) &&
                    <li className="nav-item">
                        <MenuLink className="nav-link" to={ORDERS_PATH}
                                  activeClassName="nav-item-active">
                            <FormattedMessage {...menuMessages.SPARE_PARTS}/>
                        </MenuLink>
                    </li>}
                    {(moduleRoles.isEditorDocumentation(roles) || moduleRoles.isSGEditorDocumentation(roles) || moduleRoles.isDealerDocumentation(roles)) &&
                    <li className="nav-item">
                        <MenuLink className="nav-link" to={DOCUMENTATIONS_PATH} activeClassName="nav-item-active">
                            <FormattedMessage {...menuMessages.TECH_DOC}/>
                        </MenuLink>
                    </li>}
                    {(moduleRoles.isEditorClaims(roles) || moduleRoles.isSGEditorClaims(roles) || moduleRoles.isDealerClaims(roles)) &&
                    <li className="nav-item">
                        <MenuLink className="nav-link" to={CLAIMS_PATH} activeClassName="nav-item-active">
                            <FormattedMessage {...menuMessages.CLAIMS}/>
                        </MenuLink>
                    </li>}
                </ul>
                <ul className="navbar-nav">
                    <li className="nav-item">
                        <UserNav>
                            {`${name} - ${ipn}`}
                            {moduleRoles.isAdmin(roles) &&
                            <span> - <FormattedMessage {...commonMessages.ADMIN}/></span>}
                            {(moduleRoles.isSGEditorDocumentation(roles) || moduleRoles.isSGEditorOrder(roles) || moduleRoles.isSGEditorClaims(roles)) &&
                            <span> - <FormattedMessage {...commonMessages.SG_EDITOR}/></span>}
                            {moduleRoles.isEditor(roles) &&
                            <span> - <FormattedMessage {...commonMessages.EDITOR}/></span>}
                            {(moduleRoles.isDealerOrder(roles) || moduleRoles.isDealerDocumentation(roles) || moduleRoles.isDealerClaims(roles)) &&
                            <span> - <FormattedMessage {...commonMessages.DEALER}/></span>}
                        </UserNav>
                    </li>
                    {!!ipn && <>
                        <li className="nav-item">
                            <SupportButton onClick={showSupportModal}>
                                <FontAwesomeIcon icon="cog" size="lg"/>
                            </SupportButton>
                        </li>
                        {(!process.env.REACT_APP_PROVIDER_SWITCH || localStorage.getItem("provider") === providers.OKTA) && <li className="nav-item">
                            <SupportButton onClick={() => initiateLogout()}>
                                <FontAwesomeIcon icon="power-off" size="lg"/>
                            </SupportButton>
                        </li>}
                    </>}
                </ul>
            </div>
            {isModalOpen && <SupportModal show={isModalOpen}
                                          closeModal={closeSupportModal}
                                          initialValues={{
                                              language: language.indexOf('-') >= 0 ? language.substring(0, language.indexOf('-')) : language,
                                              locale,
                                              profileIpn: ipn,
                                          }}
                                          saveModal={(value) => {
                                              if (value.profileIpn !== ipn) {
                                                  saveProfile({ipn: value.profileIpn});
                                                  sessionStorage.setItem("activeProfileSession", value.profileIpn)
                                              } else {
                                                  saveUserSettings({
                                                      language: value.language,
                                                      locale: value.locale,
                                                      ipn: ipn
                                                  });
                                              }
                                          }}
            />}
        </TopNavigation>
    );
}

TopBar.propTypes = {
    userDetail: PropTypes.object.isRequired,
    saveUserSettings: PropTypes.func.isRequired,
    saveProfile: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
    userDetail: state.profile.userDetail,
    isModalOpen: state.supportModal.isModalOpen,
    language: state.intl.language,
    locale: state.intl.locale,
});

export default withRouter(connect(mapStateToProps, {
    closeSupportModal,
    showSupportModal,
    saveUserSettings,
    saveProfile,
})(TopBar));
